/**
 * useAdminNotifications — hook per stream SSE real-time verso l'admin.
 *
 * Apre un long-lived stream verso ``/api/admin/notifications/stream``.
 * Riceve eventi JSON e chiama ``onEvent(event)`` per ciascuno.
 * Gestisce reconnect automatico con backoff esponenziale.
 *
 * Espone anche ``sseActive`` (via callback ``onStatus``) per permettere al
 * consumer di disabilitare il polling di fallback quando SSE è OPEN.
 *
 * Auth: passiamo il JWT come query param ``?token=`` perché ``fetch``+reader
 * su un lungo stream in produzione può soffrire di buffering; usiamo
 * ``EventSource`` che è la scelta più robusta per SSE (con riconnessione
 * built-in). EventSource non supporta headers custom, ecco perché usiamo
 * la query string (accettata anche da admin_notifications.py).
 */
import { useEffect, useRef } from "react";
import { BACKEND_URL } from "../lib/api";

export function useAdminNotifications({ enabled = true, onEvent, onStatus }) {
  const onEventRef = useRef(onEvent);
  const onStatusRef = useRef(onStatus);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);
  const esRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("ah_token");
      if (!token) return;

      // Chiudi eventuale connessione precedente
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* ignore */ }
        esRef.current = null;
      }

      const url = `${BACKEND_URL}/api/admin/notifications/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url, { withCredentials: false });
      esRef.current = es;

      es.onopen = () => {
        retryAttemptRef.current = 0;
        onStatusRef.current?.("open");
      };

      es.onmessage = (ev) => {
        if (!ev?.data) return;
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "ping" || payload.type === "connected") return;
          onEventRef.current?.(payload);
        } catch {
          /* ignore malformed */
        }
      };

      es.onerror = () => {
        // EventSource ritenta automaticamente, ma proxy Kubernetes può
        // interrompere la connessione — chiudi e riconnetti con backoff.
        onStatusRef.current?.("error");
        try { es.close(); } catch { /* ignore */ }
        esRef.current = null;
        if (cancelled) return;
        const attempt = ++retryAttemptRef.current;
        const delay = Math.min(30000, 1500 * 2 ** Math.min(attempt - 1, 5));
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* ignore */ }
        esRef.current = null;
      }
      onStatusRef.current?.("closed");
    };
  }, [enabled]);
}
